import type { ChainConfig, Environment, IWalletAdapter, SurfConfig, TokenConfig } from "../types";
import { cloneRegistries, getDefaultChainId } from "../config";
import { SurfClient } from "./SurfClient";
import {
  CoinbaseAdapter,
  InjectedAdapter,
  MetaMaskAdapter,
  PhantomAdapter,
  RabbyAdapter,
  TrustWalletAdapter,
  WalletConnectAdapter,
} from "../wallets";
import { validateChainConfig, validateConfig, validateTokenConfig } from "../utils/validation";

export class SurfClientBuilder {
  private config: SurfConfig = {
    projectName: "",
  };

  private readonly registries = cloneRegistries();
  private readonly walletAdapters = new Map<string, IWalletAdapter>();

  constructor() {
    this.walletAdapters.set("metamask", new MetaMaskAdapter());
    this.walletAdapters.set("trust", new TrustWalletAdapter());
    this.walletAdapters.set("coinbase", new CoinbaseAdapter());
    this.walletAdapters.set("rabby", new RabbyAdapter());
    this.walletAdapters.set("phantom", new PhantomAdapter());
    this.walletAdapters.set("walletconnect", new WalletConnectAdapter());
    this.walletAdapters.set("injected", new InjectedAdapter());
  }

  setProject(name: string, id: string): this {
    this.config.projectName = name;
    this.config.projectId = id;
    this.config.appId = id;
    return this;
  }

  setAppId(id: string): this {
    this.config.appId = id;
    this.config.projectId = id;
    return this;
  }

  setEnvironment(environment: Environment): this {
    this.config.environment = environment;
    return this;
  }

  setChain(chainId: number): this {
    this.config.chainId = chainId;
    return this;
  }

  setRpcUrl(url: string): this {
    this.config.rpcUrl = url;
    return this;
  }

  setApiBaseUrl(url: string): this {
    this.config.apiBaseUrl = url;
    return this;
  }

  setFactoryAddress(address: `0x${string}`): this {
    this.config.factoryAddress = address;
    return this;
  }

  setAutoApprove(enabled: boolean): this {
    this.config.autoApprove = enabled;
    return this;
  }

  setLogLevel(level: SurfConfig["logger"]): this {
    if (level !== undefined) {
      this.config.logger = level;
    }
    return this;
  }

  registerWalletAdapter(name: string, adapter: IWalletAdapter): this {
    this.walletAdapters.set(name, adapter);
    return this;
  }

  registerChain(environment: Environment, config: ChainConfig): this {
    validateChainConfig(config);
    this.registries[environment][config.chainId] = {
      ...config,
      tokens: [...config.tokens],
    };
    return this;
  }

  registerToken(environment: Environment, chainId: number, token: TokenConfig): this {
    validateTokenConfig(token);
    const chain = this.registries[environment][chainId];
    if (!chain) {
      throw new Error(`Cannot register token on unknown chain ${chainId} in ${environment}`);
    }
    chain.tokens.push(token);
    return this;
  }

  build(): SurfClient {
    const resolved = validateConfig(
      this.config,
      (environment, chainId) => Boolean(this.registries[environment][chainId]),
      getDefaultChainId,
    );

    const chain = this.registries[resolved.environment][resolved.chainId];
    if (!chain) {
      throw new Error(
        `Missing chain config for ${resolved.environment}:${resolved.chainId}`,
      );
    }
    return new SurfClient({
      config: {
        ...resolved,
        rpcUrl: resolved.rpcUrl || chain.rpcUrl,
        factoryAddress: resolved.factoryAddress === "0x0000000000000000000000000000000000000000"
          ? chain.factoryAddress
          : resolved.factoryAddress,
      },
      registries: this.registries,
      walletAdapters: this.walletAdapters,
    });
  }
}
